import { DIALOG_DATA } from '@angular/cdk/dialog';
import { Component, HostListener, inject, model, ModelSignal, Signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FullImageCarouselComponent } from '@app/shared/components/carousel/full-image-carousel/full-image-carousel.component';
import { ThumbnailsCarouselComponent } from '@app/shared/components/carousel/thumbnails-carousel/thumbnails-carousel.component';
import { AvatarComponent } from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { DEFAULT_DATETIME_FORMAT } from '@app/shared/configs/date-format.config';
import { ProffeoDatePipe } from '@app/shared/pipes/proffeo-date.pipe';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDividerComponent } from '@app/shared/ui/divider/ui-divider.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface CarouselDialogData {
  currentIndex?: number;
  files: Signal<FileInfo[]>;
  onFilesAdded?: (files: File[]) => void;
  onFileDeleted?: (file: FileInfo) => void;
  onDescriptionChanged?: (event: { file: FileInfo; description: string }) => void;
}

@Component({
  selector: 'proffeo-carousel-dialog',
  templateUrl: './carousel-dialog.component.html',
  imports: [
    FullImageCarouselComponent,
    ThumbnailsCarouselComponent,
    ProffeoDatePipe,
    AvatarComponent,
    UiButtonComponent,
    UiDividerComponent,
    TranslatePipe
  ]
})
export class CarouselDialogComponent {
  private readonly dialogRef: MatDialogRef<CarouselDialogComponent> = inject(MatDialogRef<CarouselDialogComponent>);

  private data: CarouselDialogData = inject(DIALOG_DATA);

  protected readonly defaultDatetimeFormat = DEFAULT_DATETIME_FORMAT;

  protected readonly AvatarSize = AvatarSize;
  protected files: Signal<FileInfo[]> = this.data.files;
  protected currentIndex: ModelSignal<number> = model<number>(0);
  protected modalId: string = crypto.randomUUID();

  public constructor() {
    this.currentIndex.set(this.data.currentIndex ?? 0);
  }

  @HostListener('window:keydown.ArrowRight')
  protected onRightArrowKey(): void {
    if (this.currentIndex() === this.files().length - 1) return;
    this.currentIndex.update(index => index + 1);
  }

  @HostListener('window:keydown.ArrowLeft')
  protected onLeftArrowKey(): void {
    if (this.currentIndex() === 0) return;
    this.currentIndex.update(index => index - 1);
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected onFilesAdded(files: File[]): void {
    if (this.data.onFilesAdded) {
      this.data.onFilesAdded(files);
    }
  }

  protected onFileDeleted(file: FileInfo): void {
    if (this.data.onFileDeleted) {
      this.data.onFileDeleted(file);
    }
  }

  protected onDescriptionChanged(event: { file: FileInfo; description: string }): void {
    if (this.data.onDescriptionChanged) {
      this.data.onDescriptionChanged(event);
    }
  }
}
